import type React from 'react'
import { Modal, Pluralize, Text } from 'ui'
import {
  countChargedExtraWorkers,
  describeExtraWorkers,
  formatChargeAfterWindow,
  formatMinimumPayPolicyLength,
} from 'pages/HomePage/ShiftCard/cushionExtraWorkers'
import { useShiftDetail } from '../useShiftDetail'

interface Props {
  trigger: React.ReactNode
}

export const CushionedWorkersModal = ({ trigger }: Props) => {
  const { shift } = useShiftDetail()

  if (!shift || !shift.workersNeeded) return null

  const assignedWork = shift.work || []
  const extraWorkers = assignedWork.length - shift.workersNeeded
  const chargeAfterMinutes =
    shift.cushionChargePolicy?.chargeAfterMinutes ?? null
  const chargedExtraWorkers = countChargedExtraWorkers({
    workers: assignedWork,
    workersNeeded: shift.workersNeeded,
    shiftStartsAt: shift.startsAt,
    chargeAfterMinutes,
  })
  const shiftHasEnded = shift.endsAt
    ? new Date(shift.endsAt).getTime() <= Date.now()
    : false
  const notice = describeExtraWorkers({
    extraWorkers,
    chargedExtraWorkers,
    chargeAfterMinutes,
    shiftHasEnded,
  })
  const minPayLengthMinutes = shift.minimumPayPolicy?.minPayLength
  const minimumPayLength =
    minPayLengthMinutes === undefined
      ? null
      : formatMinimumPayPolicyLength(minPayLengthMinutes)

  if (chargeAfterMinutes === null || !notice?.shiftSummary) {
    return (
      <Modal
        title={
          <Text>
            We've scheduled{' '}
            <Pluralize
              count={extraWorkers}
              oneText={'extra worker'}
              manyText={'extra workers'}
            />{' '}
            for you!
          </Text>
        }
        trigger={trigger}
        width={[1, 900]}
      >
        <Text mb={2}>
          Don't worry, you'll only be charged for the {shift.workersNeeded} you
          asked for. If you don't need the extra help, feel free to send them
          home. They will still be paid by WorkWhile.
        </Text>
      </Modal>
    )
  }

  const releaseWindow = formatChargeAfterWindow(chargeAfterMinutes)
  const minimumPayClause = minimumPayLength ? ` of ${minimumPayLength}` : ''

  return (
    <Modal
      title="How extra workers are billed"
      trigger={trigger}
      width={[1, 900]}
    >
      <Text mb={2}>We schedule extra workers to help cover no-shows.</Text>
      <Text mb={2}>
        If you release an extra worker within {releaseWindow} of shift start,
        you won't be billed. If they stay longer than {releaseWindow}, you're
        billed under your minimum pay policy{minimumPayClause}.
      </Text>
      <Text mb={2}>This shift: {notice.shiftSummary}</Text>
    </Modal>
  )
}
